import { Close, CloudUploadOutlined, FilePresent } from '@mui/icons-material';
import { Avatar, IconButton } from '@mui/material';
import { useField } from 'formik';
import { useMemo } from 'react';

import { ContentV1 } from '@jebel/constants';
import {
  fileSizeToHuman,
  generateContentMediaSourceSet,
  transformContentMedia,
} from '@jebel/utils';

import { useMediaDisposer, useMediaUploader } from 'shared/hooks';
import { recordError } from 'shared/utils/record';

import {
  Container,
  DropzoneContainer,
  DropzoneHelperText,
  DropzonePlaceholder,
  MediaContainer,
  MediaItemContainer,
  MediaItemDescription,
  MediaItemFilename,
  MediaItemSize,
} from './FormMediaUploader.styles';

const DEFAULT_ACCEPT = 'image/*, video/*';

export interface FormMediaUploaderProps {
  name: string;

  /**
   * The placeholder text to display when no files are uploaded.
   * @default 'Upload media'
   */
  placeholder?: string;

  /**
   * The helper text to display when the user is uploading a file.
   */
  helperText?: string;

  /**
   * The accepted file types.
   * @default 'image/*, video/*'
   */
  accept?: string;

  /**
   * The maximum number of files to upload.
   * When `undefined`, the user can upload an unlimited number of files.
   * @default undefined
   */
  maxFiles?: number;
}

export function FormMediaUploader({
  name,
  placeholder = 'Upload media',
  helperText,
  accept = DEFAULT_ACCEPT,
  maxFiles,
}: FormMediaUploaderProps) {
  const [field, , helpers] = useField<ContentV1.Media[]>({
    name,
    defaultValue: [],
  });

  const media = useMemo(() => {
    return field.value || [];
  }, [field.value]);

  const { mutateAsync: uploadMedia } = useMediaUploader();
  const { mutateAsync: disposeMedia } = useMediaDisposer();

  const missingFilesCount = useMemo(() => {
    return maxFiles ? Math.max(0, maxFiles - field.value.length) : Infinity;
  }, [maxFiles, field.value.length]);

  const removeMedia = async (id: string) => {
    const newMedia = media.filter(m => m.id !== id);

    try {
      await disposeMedia(id);
      helpers.setValue(newMedia);
    } catch (err) {
      recordError(err);
      // Rollback the change
      helpers.setValue(media);
    }
  };

  const selectFiles = async (files: File[]) => {
    const filesQueue = files.slice(0, missingFilesCount);

    for (const file of filesQueue) {
      helpers.setValue([
        ...field.value,
        // Optimistically update the form state
        {
          id: file.name,
          type: file.type,
          filename: file.name,
          provider: ContentV1.MediaProvider.Local,
          pathname: file.name,
          size: file.size,
          verificationDate: null,
        },
      ]);

      try {
        const media = await uploadMedia(file);
        helpers.setValue([...field.value, media]);
      } catch (err) {
        recordError(err);
      }
    }
  };

  const handleClickDropzone = () => {
    const input = document.createElement('input');

    input.type = 'file';
    input.accept = accept;
    input.multiple = Boolean(maxFiles && maxFiles > 1);

    input.onchange = event => {
      const target = event.target as HTMLInputElement;
      const files = target.files;

      if (!files || files.length === 0) {
        return;
      }

      selectFiles(Array.from(files));
    };

    input.click();
  };

  return (
    <Container>
      {missingFilesCount > 0 && (
        <DropzoneContainer onClick={handleClickDropzone}>
          <CloudUploadOutlined fontSize="large" />
          <DropzonePlaceholder>{placeholder}</DropzonePlaceholder>
          <DropzoneHelperText>{helperText}</DropzoneHelperText>
        </DropzoneContainer>
      )}

      {media.length > 0 && (
        <MediaContainer>
          {media.map(media => {
            const src = transformContentMedia(process.env.REACT_APP_CONTENT_API_URL!, media.id);

            const srcSet = generateContentMediaSourceSet(
              process.env.REACT_APP_CONTENT_API_URL!,
              media.id,
            );

            return (
              <MediaItemContainer key={media.id}>
                <Avatar variant="rounded" src={src} srcSet={srcSet} alt={media.filename}>
                  <FilePresent fontSize="inherit" />
                </Avatar>

                <MediaItemDescription>
                  <MediaItemFilename>{media.filename}</MediaItemFilename>
                  <MediaItemSize>{fileSizeToHuman(Number(media.size))}</MediaItemSize>
                </MediaItemDescription>

                <IconButton size="small" onClick={() => removeMedia(media.id)}>
                  <Close fontSize="inherit" />
                </IconButton>
              </MediaItemContainer>
            );
          })}
        </MediaContainer>
      )}
    </Container>
  );
}
